import React, {useEffect, useMemo, useRef, useState} from "react";
import {Bluetooth, ClipboardCheck, Info, Lock, MapPin, SlidersHorizontal, X} from "lucide-react";
import {cn} from "../../utils/cn";
import {hasPermission} from "../../utils/rolePermissions";
import {ALL_REPORT_IDS, DEFAULT_REPORT, REPORTS, REPORT_GROUPS, resolveReportId} from "./reportCatalog";

// Legacy report components — preserved as-is, rendered in the content pane.
import LockUnlockReportSearch from "../../component/LockUnlockReportSearch";
import TaskReport from "../../component/TaskManagement/TaskReport";
import TrackingReport from "../../component/Customer/TrackingReport";
import BTLockUnlockReport from "../../component/BTLockUnlockReport";
import Gt06TrackerReport from "../../component/Gt06TrackerReport";
import Gt06DailyActivity from "../../component/Gt06Reports/Gt06DailyActivity";
import Gt06FleetDistance from "../../component/Gt06Reports/Gt06FleetDistance";
import Gt06Trips from "../../component/Gt06Reports/Gt06Trips";
import Gt06AlertLog from "../../component/Gt06Reports/Gt06AlertLog";
import ListAuditLogs from "../../component/AuditLog/ListAuditLogs";

/**
 * Map of catalog id -> rendered component. Keeps the existing report
 * components untouched; only the chrome (device segments + report tabs
 * + filter drawer) is new.
 */
const COMPONENT_FOR_ID = {
  GPS_LOCK_UNLOCK:    () => <LockUnlockReportSearch />,
  TASK_REPORT:        () => <TaskReport />,
  ASSET_TRACKING:     () => <TrackingReport />,
  BT_LOCK_UNLOCK:     () => <BTLockUnlockReport />,
  GT06_TRIPS:         () => <Gt06Trips />,
  GT06_DAILY_ACTIVITY:() => <Gt06DailyActivity />,
  GT06_FLEET_DISTANCE:() => <Gt06FleetDistance />,
  GT06_ALERT_LOG:     () => <Gt06AlertLog />,
  GT06_FRAME_LOG:     () => <Gt06TrackerReport />,
  AUDIT_TRAIL:        () => <ListAuditLogs isSuperAdmin={localStorage.getItem("role") === "SUPER_ADMIN"} />,
};

/** Device-type icon per catalog group, shown in the segmented control. */
const GROUP_ICON = {
  "GPS Lock":    Lock,
  "BT Lock":     Bluetooth,
  "GPS Tracker": MapPin,
  "Audit":       ClipboardCheck,
};

/**
 * Reports whose legacy component does NOT render a `.report-filter-sidebar`
 * — the Filters toggle is hidden for these.
 */
const NO_FILTER_DRAWER = new Set(["AUDIT_TRAIL"]);

const GROUP_KEY = "yk_reports_group_v1";
const SELECTED_KEY_V1 = "yk_reports_selected_v1";   // legacy single-id key, used as migration seed
const SELECTED_KEY_V2 = "yk_reports_selected_v2";   // JSON map: group -> report id

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    return v && typeof v === "object" ? v : fallback;
  } catch { return fallback; }
}

function writeStorage(key, value) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

/**
 * Reports layout (Phase 6 — horizontal chrome):
 *   Row 1 — device-type segmented control (GPS Lock / BT Lock / GPS
 *           Tracker / Audit) + About + Filters toggle.
 *   Row 2 — report tabs scoped to the active device type.
 *   Body  — the legacy report at full width. Each legacy report's
 *           built-in `.report-filter-sidebar` is restyled (see
 *           reports.css "Shell v2") into a right-hand overlay drawer
 *           controlled by the Filters toggle, so the data table always
 *           gets the full horizontal span.
 *
 * Permissions:
 *   - Each REPORTS[id].permission may be a string or array. If set, the
 *     tab is shown only when the user has at least one of those perms.
 *     Device segments with no visible reports are hidden entirely.
 */
export default function ReportsLayout() {
  const [showHelp, setShowHelp] = useState(false);

  const canViewReport = (id) => {
    const perm = REPORTS[id]?.permission;
    if (!perm) return true;
    const list = Array.isArray(perm) ? perm : [perm];
    return list.some(hasPermission);
  };

  const visibleIds = useMemo(() => ALL_REPORT_IDS.filter(canViewReport), []);
  const visibleGroups = useMemo(
    () => REPORT_GROUPS.filter((g) => visibleIds.some((id) => REPORTS[id].group === g)),
    [visibleIds]
  );

  // Per-group tab memory: { "GPS Lock": "TASK_REPORT", ... }
  const [selectedByGroup, setSelectedByGroup] = useState(() => {
    const map = readJson(SELECTED_KEY_V2, {});
    // Remembered tabs for retired reports (e.g. Trip Route / Trip Summary -> Trips).
    for (const g of Object.keys(map)) map[g] = resolveReportId(map[g]);
    // Migrate the old single-selection key once, then let v2 own it.
    try {
      const v1 = resolveReportId(localStorage.getItem(SELECTED_KEY_V1));
      if (v1 && REPORTS[v1] && !map[REPORTS[v1].group]) map[REPORTS[v1].group] = v1;
    } catch { /* ignore */ }
    return map;
  });

  const [activeGroup, setActiveGroup] = useState(() => {
    try {
      const stored = localStorage.getItem(GROUP_KEY);
      if (stored && visibleGroups.includes(stored)) return stored;
    } catch { /* ignore */ }
    const def = REPORTS[DEFAULT_REPORT]?.group;
    return visibleGroups.includes(def) ? def : visibleGroups[0];
  });

  // The filter drawer ALWAYS starts open — it is the only affordance that
  // explains how to use a report (pick devices, pick dates, Search), so a
  // first-time user must land on it. It also reopens on every device-type /
  // report-tab switch. The open/closed state is intentionally NOT persisted:
  // an earlier version remembered "closed" in localStorage, which left users
  // staring at an empty table with no visible way in.
  const [filtersOpen, setFiltersOpen] = useState(true);

  const setFilters = (open) => setFiltersOpen(open);

  const groupIds = useMemo(
    () => visibleIds.filter((id) => REPORTS[id].group === activeGroup),
    [visibleIds, activeGroup]
  );

  // Resolve the active report: remembered tab for this group, validated
  // against current visibility, else the group's first visible report.
  const remembered = selectedByGroup[activeGroup];
  const selected = (remembered && groupIds.includes(remembered)) ? remembered : groupIds[0];

  const pickGroup = (group) => {
    setActiveGroup(group);
    setShowHelp(false);
    setFilters(true); // surface the filters for the newly chosen report
    writeStorage(GROUP_KEY, group);
  };

  const pickReport = (id) => {
    setShowHelp(false);
    setFilters(true); // surface the filters for the newly chosen report
    setSelectedByGroup((prev) => {
      const next = {...prev, [REPORTS[id].group]: id};
      writeStorage(SELECTED_KEY_V2, JSON.stringify(next));
      return next;
    });
    writeStorage(SELECTED_KEY_V1, id); // keep v1 in sync for back-compat
  };

  const active = REPORTS[selected] || REPORTS[DEFAULT_REPORT];
  const ActiveComponent = COMPONENT_FOR_ID[active.id] || (() => null);
  const hasFilterDrawer = !NO_FILTER_DRAWER.has(active.id);

  // On narrow screens both tab rows scroll horizontally and overflow off
  // both edges with no scrollbar — switching tabs near an edge silently
  // moved the strip without the user noticing. Bring the newly active tab
  // fully into view (centered) every time the selection changes, on either
  // device width, so the active tab is always where you just tapped it.
  const groupTabsRef = useRef(null);
  const reportTabsRef = useRef(null);

  useEffect(() => {
    const el = groupTabsRef.current?.querySelector('[aria-selected="true"]');
    el?.scrollIntoView({behavior: "smooth", inline: "center", block: "nearest"});
  }, [activeGroup]);

  useEffect(() => {
    const el = reportTabsRef.current?.querySelector('[aria-selected="true"]');
    el?.scrollIntoView({behavior: "smooth", inline: "center", block: "nearest"});
  }, [selected]);

  // The legacy "Fetch Reports" button lives inside the drawer
  // (`.btn-report`, primary variant). Auto-close the drawer when it is
  // clicked so the freshly fetched table gets the whole width. "Clear
  // Filters" (`.btn-report.secondary`) keeps the drawer open.
  const onContentClickCapture = (e) => {
    const btn = e.target.closest?.(".btn-report");
    if (btn && !btn.classList.contains("secondary")) setFilters(false);
  };

  return (
    <div
      className={cn(
        "reports-shell-root flex-1 min-w-0 min-h-0 flex flex-col bg-surface-muted overflow-hidden",
        filtersOpen ? "yk-filters-open" : "yk-filters-closed"
      )}
    >

      {/* ── Chrome band: device segments + actions, then report tabs ── */}
      <header className="bg-white border-b border-ink-200 shrink-0">
        <div className="px-3 sm:px-4 pt-2.5 pb-2 flex items-center justify-between gap-3">
          {/* Device-type segmented control */}
          <div
            ref={groupTabsRef}
            role="tablist"
            aria-label="Device type"
            className="yk-tabbar yk-tabbar-snap inline-flex items-center gap-1 rounded-lg bg-ink-100/70 p-1 overflow-x-auto max-w-full"
          >
            {visibleGroups.map((group) => {
              const Icon = GROUP_ICON[group] || Lock;
              const isActive = group === activeGroup;
              // NOTE: deliberately no count badge here. An earlier version
              // showed the number of report TYPES in the group ("GPS
              // Tracker 6"), which users read as a device count that
              // contradicted the dashboard's real fleet numbers.
              return (
                <button
                  key={group}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => pickGroup(group)}
                  className={cn(
                    "yk-tab-snap shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm whitespace-nowrap",
                    "transition-colors duration-fast",
                    isActive
                      ? "bg-white text-brand-700 font-semibold shadow-xs"
                      : "text-ink-600 font-medium hover:text-ink-900"
                  )}
                >
                  <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-brand-700" : "text-ink-500")} aria-hidden="true" />
                  {group}
                </button>
              );
            })}
          </div>

          {/* Right actions: About + Filters */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              aria-expanded={showHelp}
              className={cn(
                "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-sm font-medium",
                showHelp ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:bg-ink-100 hover:text-ink-900"
              )}
              title="About this report"
            >
              <Info className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">About</span>
            </button>
            {hasFilterDrawer && (
              <button
                type="button"
                onClick={() => setFilters(!filtersOpen)}
                aria-expanded={filtersOpen}
                className={cn(
                  "inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-sm font-medium",
                  "transition-colors duration-fast",
                  filtersOpen
                    ? "bg-brand-50 border-brand-200 text-brand-700"
                    : "bg-white border-ink-200 text-ink-700 hover:bg-ink-50"
                )}
              >
                {filtersOpen
                  ? <X className="h-4 w-4" aria-hidden="true" />
                  : <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />}
                Filters
              </button>
            )}
          </div>
        </div>

        {/* Report tabs for the active device type */}
        <nav
          ref={reportTabsRef}
          role="tablist"
          aria-label={`${activeGroup} reports`}
          className="yk-tabbar yk-tabbar-snap px-3 sm:px-4 flex items-end gap-1 overflow-x-auto"
        >
          {groupIds.map((id) => {
            const r = REPORTS[id];
            const isActive = id === selected;
            const Icon = r.icon;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => pickReport(id)}
                className={cn(
                  "yk-tab-snap shrink-0 inline-flex items-center gap-1.5 h-9 px-3 -mb-px border-b-2 text-sm whitespace-nowrap",
                  "transition-colors duration-fast",
                  isActive
                    ? "border-accent-500 text-ink-900 font-semibold"
                    : "border-transparent text-ink-500 font-medium hover:text-ink-800 hover:border-ink-300"
                )}
              >
                <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-accent-500" : "text-ink-400")} aria-hidden="true" />
                {r.label}
              </button>
            );
          })}
        </nav>

        {showHelp && active.description && (
          <div className="mx-3 sm:mx-4 mb-2.5 rounded-lg bg-brand-50 border border-brand-100 px-3 py-2 text-sm text-brand-900">
            <span className="font-semibold">{active.label}.</span> {active.description}
          </div>
        )}
      </header>

      {/* ── Content: legacy report, full width. The shell classes turn the
            report's built-in filter sidebar into an overlay drawer. ── */}
      <div
        className={cn(
          // flex-col so the legacy report's `.report-content-split`
          // (flex: 1) actually stretches to the bottom of the viewport
          // instead of collapsing to its content height.
          "flex-1 min-h-0 flex flex-col overflow-y-auto px-2 sm:px-3 py-3 legacy-in-slideover yk-report-shell",
          filtersOpen ? "yk-filters-open" : "yk-filters-closed"
        )}
        onClickCapture={onContentClickCapture}
      >
        <ActiveComponent />
      </div>
    </div>
  );
}
